import compact from "lodash/compact";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";

import {
  ConversationModel,
  getConversationIdentifier as getConversationModelIdentifier,
  isConversationSupportConversation as isConversationModelSupportConversation,
} from "./ConversationModel";
import { useMessengerContext } from "./MessengerContext";
import { useMessengerStoreMainStreamFetchState } from "./useMessengerStoreMainStreamFetchState";
import {
  conversationRetailerContextComparator,
  conversationBrandContextComparator,
  conversationLastModifiedComparator,
} from "./utils";

export const useMessengerStoreAllOrderedConversations = () => {
  const { retailerContext, brandContext } = useMessengerContext() ?? {};
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();

  const allOrderedConversations = useMemo(() => {
    if (!mainStreamFetchState) {
      return [];
    }
    return uniqBy(
      compact(
        Array.from(mainStreamFetchState.conversationByUserToken?.values() ?? [])
      ),
      (conversation: ConversationModel) =>
        getConversationModelIdentifier(conversation)
    ).sort(
      (conversationA: ConversationModel, conversationB: ConversationModel) => {
        // support conversation comes first
        if (
          isConversationModelSupportConversation(conversationA) !==
          isConversationModelSupportConversation(conversationB)
        ) {
          return isConversationModelSupportConversation(conversationA) ? -1 : 1;
        }

        const retailerContextComparator = conversationRetailerContextComparator(
          conversationA,
          conversationB,
          retailerContext?.owner_user_token
        );

        if (retailerContextComparator !== 0) {
          return retailerContextComparator;
        }

        const brandContextComparator = conversationBrandContextComparator(
          conversationA,
          conversationB,
          brandContext?.token
        );
        if (brandContextComparator !== 0) {
          return brandContextComparator;
        }

        return conversationLastModifiedComparator(conversationA, conversationB);
      }
    );
  }, [
    brandContext?.token,
    mainStreamFetchState,
    retailerContext?.owner_user_token,
  ]);

  return { allOrderedConversations };
};
